import IAction from '@shared/types/interfaces/action';
import ListContext from '@shared/components/utils/context/listContext';
import MediaProduction from '@shared/classes/mediaProduction';
import Props from '@shared/types/props/list';
import Routes from '@shared/types/enums/routes';
import mosaicKeyboardHandler from '@shared/components/utils/helpers/mosaicKeyboard';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { Coordinates } from '@shared/utils/floatingMenu';
import { RailType } from '@shared/types/enums/rail';
import { focusSelector } from '@shared/store/slices/focus.slice';
import { openFloatingMenu } from '@shared/store/thunks/floatingMenu.thunk';
import { store } from '@shared/store/store';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useState, useRef, ReactElement, useEffect } from 'react';
import {
  routerSelector,
  updateParams,
} from '@shared/store/slices/router.slice';

const OFFSET = 344;

export default function Mosaic({
  children,
  data,
  floatingMenu = true,
  focus,
  lowerBound,
  maxCols,
  stopPropagation,
  onChange,
  outOfBounds,
}: Props) {
  const dispatch = useAppDispatch();
  const { focus: currentFocus } = useAppSelector(focusSelector);
  const {
    params: { current: currentParams },
  } = useAppSelector(routerSelector);
  const [index, setIndex] = useState((currentParams?.index as number) || 0);

  // Refs

  const dataRef = useRef(data);
  const focusedRef = useRef<boolean>(false);
  const indexRef = useRef<number>(index);
  const lengthRef = useRef(children?.length);

  dataRef.current = data;
  focusedRef.current =
    focus === currentFocus ||
    ((currentFocus as (number | string)[])?.length
      ? (currentFocus as (number | string)[]).includes(focus + '') ||
        (currentFocus as (number | string)[]).includes(focus as number)
      : false);
  indexRef.current = index;
  lengthRef.current = children?.length;

  // Keyboard hooks

  useKeyboard({
    focus,
    stopPropagation,
    keyDownHandler: (key: string) => {
      mosaicKeyboardHandler({
        indexRef,
        key,
        length: lengthRef.current || 0,
        lowerBound,
        maxCols: maxCols || 10,
        onChange,
        outOfBounds,
        setIndex,
      });
    },
    keyUpHandler: (key: string) => {
      const { focus: focusSlice } = store.getState();
      if (
        key === 'SELECT' &&
        children &&
        (focusSlice.focus === focus ||
          (focusSlice.focus as number[]).includes(focus || -1))
      ) {
        const child = children[indexRef.current] as ReactElement<IAction>;
        if (child.props?.action) {
          child?.props.action();
        }
      }
    },
    keyPressHandler: () => {
      const {
        floatingMenu: { open },
      } = store.getState();

      if (dataRef.current && floatingMenu && !open) {
        if (indexRef.current >= 0) {
          const coordinates = {
            x: Math.min(
              Coordinates['MOSAIC'].x +
                OFFSET * (indexRef.current % (maxCols || 1)),
              Coordinates['MOSAIC_RIGHT_BORDER'].x
            ),
            y:
              indexRef.current === (maxCols || 1) - 1
                ? Coordinates['MOSAIC_RIGHT_BORDER'].y
                : Coordinates['MOSAIC'].y,
          };
          dispatch(updateParams({ index: indexRef.current }));
          dispatch(
            openFloatingMenu({
              route: Routes.MAIN,
              asset: dataRef.current[indexRef.current] as MediaProduction,
              position: coordinates,
              type: RailType.REGULAR,
            })
          );
        }
      }
    },
  });

  useEffect(() => {
    if (data?.length && data.length <= indexRef.current && onChange) {
      const newIndex = data.length - 1;
      const newRow = Math.floor(maxCols ? newIndex / maxCols : 0);
      setIndex(newIndex);
      onChange(newIndex, newRow);
    }
  }, [data]);

  // Context provider

  const value = {
    listIndex: index,
    listFocused: focusedRef.current,
    setListIndex: (newIndex: number) => {
      setIndex(newIndex);
    },
  };

  return <ListContext.Provider value={value}>{children}</ListContext.Provider>;
}
